import { useEffect, useState, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Live presence indicator. Sends a heartbeat every 15s and polls the list
 * of active sessions every 10s. Returns the array of OTHER users currently
 * viewing the same resource (the calling user is excluded server-side).
 *
 * On unmount, fires a best-effort DELETE to remove our presence row early.
 *
 * @param {string} resourceType e.g. 'prospect' | 'client' | 'devis'
 * @param {string|null} resourceId — null/undefined disables the hook
 */
export default function usePresence(resourceType, resourceId) {
    const [others, setOthers] = useState([]);
    const aliveRef = useRef(true);

    useEffect(() => {
        if (!resourceType || !resourceId) {
            setOthers([]);
            return;
        }
        aliveRef.current = true;

        const heartbeat = async () => {
            try {
                await axios.post(
                    `${API_URL}/api/presence/heartbeat`,
                    { resource_type: resourceType, resource_id: resourceId },
                    { withCredentials: true },
                );
            } catch {
                /* network blips ok */
            }
        };

        const poll = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/presence`, {
                    params: { resource_type: resourceType, resource_id: resourceId },
                    withCredentials: true,
                });
                if (aliveRef.current) setOthers(Array.isArray(res.data) ? res.data : []);
            } catch {
                /* ignore */
            }
        };

        heartbeat();
        poll();
        const hbId = setInterval(heartbeat, 15000);
        const pollId = setInterval(poll, 10000);

        return () => {
            aliveRef.current = false;
            clearInterval(hbId);
            clearInterval(pollId);
            // Best-effort cleanup
            axios
                .delete(`${API_URL}/api/presence`, {
                    params: { resource_type: resourceType, resource_id: resourceId },
                    withCredentials: true,
                })
                .catch(() => { });
        };
    }, [resourceType, resourceId]);

    return others;
}
